import React, { forwardRef } from "react";
import { IoCopyOutline } from "react-icons/io5";

const COLOR_SCHEMES = {
  default: {
    key: "text-[#e06c75]", // Pink
    string: "text-[#98c379]", // Green
    number: "text-[#d19a66]", // Orange
    boolean: "text-[#d19a66]", // Orange
    null: "text-[#d19a66]", // Orange
    punctuation: "text-[#abb2bf]", // Light gray
    keyword: "text-[#c678dd]", // Purple
  },
  bubble: {
    key: "text-white font-semibold",
    string: "text-yellow-200",
    number: "text-sky-200",
    boolean: "text-sky-200",
    null: "text-sky-200",
    punctuation: "text-white/70",
    keyword: "text-white",
  },
};

const SyntaxHighlight = ({ children, type, colorScheme = "default" }) => {
  const colorMap = COLOR_SCHEMES[colorScheme] ?? COLOR_SCHEMES.default;
  return <span className={colorMap[type]}>{children}</span>;
};

const FormattedJSON = forwardRef(({ json, variant = "default" }, ref) => {
  const colorScheme = variant === "bubble" ? "bubble" : "default";

  const copyToClipboard = () => {
    navigator.clipboard.writeText(json);
  };

  const renderJSONContent = (obj, indent = 0) => {
    if (typeof obj !== "object" || obj === null) {
      if (typeof obj === "string")
        return (
          <SyntaxHighlight type="string" colorScheme={colorScheme}>
            "{obj}"
          </SyntaxHighlight>
        );
      if (typeof obj === "number")
        return (
          <SyntaxHighlight type="number" colorScheme={colorScheme}>
            {obj}
          </SyntaxHighlight>
        );
      if (typeof obj === "boolean")
        return (
          <SyntaxHighlight type="boolean" colorScheme={colorScheme}>
            {obj.toString()}
          </SyntaxHighlight>
        );
      if (obj === null)
        return (
          <SyntaxHighlight type="null" colorScheme={colorScheme}>
            null
          </SyntaxHighlight>
        );
    }

    const isArray = Array.isArray(obj);
    const openBracket = isArray ? "[" : "{";
    const closeBracket = isArray ? "]" : "}";

    return (
      <>
        <SyntaxHighlight type="punctuation" colorScheme={colorScheme}>
          {openBracket}
        </SyntaxHighlight>
        {Object.entries(obj).map(([key, value], index, array) => (
          <React.Fragment key={key}>
            <br />
            {"  ".repeat(indent + 1)}
            {!isArray && (
              <>
                <SyntaxHighlight type="key" colorScheme={colorScheme}>
                  "{key}"
                </SyntaxHighlight>
                <SyntaxHighlight type="punctuation" colorScheme={colorScheme}>
                  :{" "}
                </SyntaxHighlight>
              </>
            )}
            {renderJSONContent(value, indent + 1)}
            {index < array.length - 1 && (
              <SyntaxHighlight type="punctuation" colorScheme={colorScheme}>
                ,
              </SyntaxHighlight>
            )}
          </React.Fragment>
        ))}
        <br />
        {"  ".repeat(indent)}
        <SyntaxHighlight type="punctuation" colorScheme={colorScheme}>
          {closeBracket}
        </SyntaxHighlight>
      </>
    );
  };

  const parsedJSON = JSON.parse(json);

  return (
    <div className="relative group">
      {variant !== "bubble" && (
        <button
          onClick={copyToClipboard}
          className="absolute right-1 top-1 bg-gray-800 text-gray-300 px-2 py-1 rounded opacity-0 group-hover:opacity-100 hover:text-white transition-opacity"
        >
          <IoCopyOutline size={16} />
        </button>
      )}
      <pre
        className={`p-2 rounded overflow-x-auto dark-slim-scrollbar ${variant !== "bubble" ? "transition-colors duration-200 group-hover:bg-gray-800" : ""}`}
      >
        <code className="text-xs">{renderJSONContent(parsedJSON)}</code>
      </pre>
    </div>
  );
});

export default FormattedJSON;
